import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Plus, Pencil, Trash2, ArrowUp, ArrowDown, Loader2, AlertCircle,
  EyeOff, ArrowLeft, X,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@pgs-core/ui/components/card";
import { Button } from "@pgs-core/ui/components/button";
import { Badge } from "@pgs-core/ui/components/badge";
import { Input } from "@pgs-core/ui/components/input";
import { Label } from "@pgs-core/ui/components/label";
import { Alert, AlertTitle, AlertDescription } from "@pgs-core/ui/components/alert";
import {
  Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription,
} from "@pgs-core/ui/components/dialog";
import { ConfirmDeleteDialog } from "@pgs-core/ui/components/confirm-dialog";
import { LoadingSpinner } from "@pgs-core/ui/components/loading-spinner";
import { PageHeader } from "@pgs-core/ui/components/page-header";
import { MenuModuleGuard } from "../components/menu-module-shell";
import { MenuItemDialog, type MenuItemDialogInitial } from "../components/menu-item-dialog";
import { moveItem } from "../lib/menus";
import {
  fetchMenuModel, updateMenu,
  createSection, updateSection, deleteSection, reorderSections,
  createItem, updateItem, attachItem, detachItem, reorderSectionItems,
  priceSummary, ALLERGEN_LABELS, DIETARY_LABELS,
  type ItemFields, type MenuItem, type MenuModel, type MenuSection,
} from "../lib/menu-module";
import type { ClientOutletContext } from "../lib/outlet-context";

/**
 * "Add item" flow for a section: pick an existing dish from the library
 * (excluding ones already in this section) or switch to creating a new one
 * inline — the new dish lands in the library AND on this section.
 */
function AddItemDialog({ open, onClose, libraryItems, sectionItemIds, onAttach, onCreateNew, busy }: {
  open: boolean;
  onClose: () => void;
  libraryItems: MenuItem[];
  sectionItemIds: string[];
  onAttach: (item: MenuItem) => void;
  onCreateNew: () => void;
  busy: boolean;
}) {
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (open) setSearch("");
  }, [open]);

  const candidates = useMemo(() => {
    const inSection = new Set(sectionItemIds);
    const term = search.trim().toLowerCase();
    return libraryItems
      .filter((item) => !inSection.has(item.id))
      .filter((item) => !term || item.name.toLowerCase().includes(term));
  }, [libraryItems, sectionItemIds, search]);

  return (
    <Dialog open={open} onOpenChange={(next: boolean) => !next && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add an item to this section</DialogTitle>
          <DialogDescription>
            Pick a dish from your items list, or create a brand new one.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <Input
            value={search}
            autoFocus
            placeholder="Search your items…"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
          />
          <div className="max-h-64 space-y-1 overflow-y-auto">
            {candidates.length === 0 && (
              <p className="py-4 text-center text-sm text-muted-foreground">
                {libraryItems.length === 0
                  ? "No items yet — create your first dish below."
                  : "No matching items (or they're all in this section already)."}
              </p>
            )}
            {candidates.map((item) => (
              <button
                key={item.id}
                type="button"
                disabled={busy}
                onClick={() => onAttach(item)}
                className="flex w-full items-center justify-between gap-3 rounded-md border px-3 py-2 text-left text-sm hover:bg-muted/60 disabled:opacity-50"
              >
                <span className="min-w-0">
                  <span className="block truncate font-medium">{item.name}</span>
                  {item.description && (
                    <span className="block truncate text-xs text-muted-foreground">{item.description}</span>
                  )}
                </span>
                <span className="shrink-0 text-xs text-muted-foreground">{priceSummary(item)}</span>
              </button>
            ))}
          </div>
        </div>

        <DialogFooter className="sm:justify-between">
          <Button variant="outline" onClick={onCreateNew} disabled={busy}>
            <Plus className="h-4 w-4" />
            Create a new item
          </Button>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MenuEditor({ api, refreshStatus, setBreadcrumbs }: Pick<ClientOutletContext, "api" | "refreshStatus" | "setBreadcrumbs">) {
  const { menuId } = useParams();

  const [model, setModel] = useState<MenuModel | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  // Dialog state
  const [menuDialog, setMenuDialog] = useState(false);
  const [menuForm, setMenuForm] = useState({ name: "", description: "" });
  const [sectionDialog, setSectionDialog] = useState<{ section?: MenuSection } | null>(null); // {} create, { section } edit
  const [sectionForm, setSectionForm] = useState({ name: "", description: "" });
  const [dialogError, setDialogError] = useState("");
  const [addTarget, setAddTarget] = useState<MenuSection | null>(null); // section for AddItemDialog
  const [itemDialog, setItemDialog] = useState<{ sectionId?: string; item?: MenuItem } | null>(null);
  const [deleteSectionTarget, setDeleteSectionTarget] = useState<MenuSection | null>(null);

  const load = useCallback(() => {
    if (!api) return;
    fetchMenuModel(api)
      .then(setModel)
      .catch((err: Error) => setError(err.message));
  }, [api]);

  useEffect(() => {
    load();
  }, [load]);

  const menu = model?.menus.find((entry) => entry.id === menuId);

  // Publish this menu's trail into the shell header (Menus › name), clearing
  // it when we leave the editor.
  useEffect(() => {
    if (!setBreadcrumbs) return;
    setBreadcrumbs(menu ? [{ label: "Menus", href: "/menu" }, { label: menu.name }] : null);
    return () => setBreadcrumbs(null);
  }, [setBreadcrumbs, menu?.name]);

  /** Run a mutation, then refetch and refresh the publish badge. */
  const mutate = async (action: () => Promise<unknown>, onError?: (message: string) => void) => {
    setBusy(true);
    setError("");
    try {
      await action();
      load();
      refreshStatus?.();
      return true;
    } catch (err) {
      (onError ?? setError)(err instanceof Error ? err.message : String(err));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const saveMenuDetails = async () => {
    if (!api || !menu) return;
    const name = menuForm.name.trim();
    if (!name) {
      setDialogError("Enter a menu name.");
      return;
    }
    const ok = await mutate(
      () => updateMenu(api, menu.id, {
        name,
        description: menuForm.description.trim() || null,
      }),
      setDialogError
    );
    if (ok) setMenuDialog(false);
  };

  const saveSection = async () => {
    if (!api || !menu) return;
    const name = sectionForm.name.trim();
    if (!name) {
      setDialogError("Enter a section title.");
      return;
    }
    const fields = { name, description: sectionForm.description.trim() || null };
    const ok = await mutate(
      () => sectionDialog?.section
        ? updateSection(api, sectionDialog.section.id, fields)
        : createSection(api, menu.id, fields),
      setDialogError
    );
    if (ok) setSectionDialog(null);
  };

  const moveSection = async (index: number, delta: -1 | 1) => {
    if (!api || !menu) return;
    const next = moveItem(menu.sections, index, delta);
    if (next === menu.sections) return;
    await mutate(() => reorderSections(api, menu.id, next.map((section) => section.id)));
  };

  const moveRow = async (section: MenuSection, index: number, delta: -1 | 1) => {
    if (!api) return;
    const next = moveItem(section.items, index, delta);
    if (next === section.items) return;
    await mutate(() => reorderSectionItems(api, section.id, next.map((item) => item.id)));
  };

  const saveItem = async (fields: ItemFields) => {
    if (!api) return;
    const ok = await mutate(
      () => itemDialog?.item
        ? updateItem(api, itemDialog.item.id, fields)
        : createItem(api, fields, itemDialog?.sectionId),
      setDialogError
    );
    if (ok) setItemDialog(null);
  };


  if (error && !model) {
    return (
      <div>
        <PageHeader title="Menu" />
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Something went wrong</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  // A loaded model implies a configured API — this narrows `api` for the JSX.
  if (!api || !model) {
    return <LoadingSpinner className="py-20" />;
  }

  if (!menu) {
    return (
      <div>
        <PageHeader title="Menu not found" />
        <Button asChild variant="outline" size="sm">
          <Link to="/menu">
            <ArrowLeft className="h-4 w-4" />
            Back to menus
          </Link>
        </Button>
      </div>
    );
  }

  const itemDialogInitial: MenuItemDialogInitial | null = itemDialog?.item
    ? {
      ...itemDialog.item,
      imageUrl: itemDialog.item.image_r2_key && model.assetsBaseUrl
        ? `${model.assetsBaseUrl}/${itemDialog.item.image_r2_key}`
        : null,
    }
    : null;

  return (
    <div>
      <PageHeader
        title={menu.name}
        description={menu.description || "Sections and dishes on this menu."}
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setMenuForm({ name: menu.name, description: menu.description ?? "" });
                setDialogError("");
                setMenuDialog(true);
              }}
            >
              <Pencil className="h-4 w-4" />
              Edit details
            </Button>
            <Button
              size="sm"
              onClick={() => {
                setSectionForm({ name: "", description: "" });
                setDialogError("");
                setSectionDialog({});
              }}
            >
              <Plus className="h-4 w-4" />
              Add section
            </Button>
          </div>
        }
      />

      {!menu.active && (
        <Alert className="mb-4">
          <EyeOff className="h-4 w-4" />
          <AlertTitle>This menu is hidden</AlertTitle>
          <AlertDescription>It won't appear on your site until you show it from the Menus list.</AlertDescription>
        </Alert>
      )}

      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Something went wrong</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {menu.sections.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            No sections yet — add one (e.g. Starters) and start placing dishes on it.
          </CardContent>
        </Card>
      )}

      <div className="space-y-4">
        {menu.sections.map((section, index) => (
          <Card key={section.id}>
            <CardHeader className="flex-row items-center justify-between space-y-0 pb-2">
              <div>
                <CardTitle className="text-base">{section.name}</CardTitle>
                {section.description && (
                  <p className="mt-0.5 text-sm text-muted-foreground">{section.description}</p>
                )}
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost" size="sm" aria-label="Move section up"
                  disabled={index === 0 || busy}
                  onClick={() => moveSection(index, -1)}
                >
                  <ArrowUp className="h-3.5 w-3.5" />
                </Button>
                <Button
                  variant="ghost" size="sm" aria-label="Move section down"
                  disabled={index === menu.sections.length - 1 || busy}
                  onClick={() => moveSection(index, 1)}
                >
                  <ArrowDown className="h-3.5 w-3.5" />
                </Button>
                <Button
                  variant="ghost" size="sm" aria-label="Edit section"
                  onClick={() => {
                    setSectionForm({ name: section.name, description: section.description ?? "" });
                    setDialogError("");
                    setSectionDialog({ section });
                  }}
                >
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button
                  variant="ghost" size="sm" aria-label="Delete section"
                  className="text-muted-foreground hover:text-destructive"
                  onClick={() => setDeleteSectionTarget(section)}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              {section.items.length === 0 ? (
                <p className="py-3 text-sm text-muted-foreground">No dishes in this section yet.</p>
              ) : (
                <ul className="divide-y">
                  {section.items.map((item, itemIndex) => (
                    <li key={item.id} className="flex items-center gap-3 py-2.5">
                      {item.image_r2_key && model.assetsBaseUrl && (
                        <img
                          src={`${model.assetsBaseUrl}/${item.image_r2_key}`}
                          alt=""
                          className="h-10 w-10 shrink-0 rounded-md object-cover"
                        />
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="flex items-center gap-2 text-sm font-medium">
                          <span className="truncate">{item.name}</span>
                          {!item.active && (
                            <Badge variant="secondary" className="shrink-0 gap-1">
                              <EyeOff className="h-3 w-3" />
                              Hidden
                            </Badge>
                          )}
                        </p>
                        {(item.allergens.length > 0 || item.dietary_tags.length > 0) && (
                          <p className="truncate text-xs text-muted-foreground">
                            {[
                              ...item.dietary_tags.map((tag) => DIETARY_LABELS[tag] ?? tag),
                              ...item.allergens.map((tag) => ALLERGEN_LABELS[tag] ?? tag),
                            ].join(" · ")}
                          </p>
                        )}
                      </div>
                      <span className="shrink-0 text-sm tabular-nums text-muted-foreground">
                        {priceSummary(item)}
                      </span>
                      <div className="flex shrink-0 items-center gap-1">
                        <Button
                          variant="ghost" size="sm" aria-label="Move item up"
                          disabled={itemIndex === 0 || busy}
                          onClick={() => moveRow(section, itemIndex, -1)}
                        >
                          <ArrowUp className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="ghost" size="sm" aria-label="Move item down"
                          disabled={itemIndex === section.items.length - 1 || busy}
                          onClick={() => moveRow(section, itemIndex, 1)}
                        >
                          <ArrowDown className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="ghost" size="sm" aria-label="Edit item"
                          onClick={() => { setDialogError(""); setItemDialog({ item }); }}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="ghost" size="sm" aria-label="Remove from this section"
                          title="Remove from this section (stays in your items list)"
                          className="text-muted-foreground hover:text-destructive"
                          disabled={busy}
                          onClick={() => mutate(() => detachItem(api, section.id, item.id))}
                        >
                          <X className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
              <Button
                variant="outline"
                size="sm"
                className="mt-3"
                onClick={() => setAddTarget(section)}
              >
                <Plus className="h-4 w-4" />
                Add item
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Edit menu details */}
      <Dialog open={menuDialog} onOpenChange={setMenuDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit menu details</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="edit-menu-name">Name</Label>
              <Input
                id="edit-menu-name"
                value={menuForm.name}
                maxLength={100}
                autoFocus
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMenuForm((prev) => ({ ...prev, name: e.target.value }))}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="edit-menu-description">Description (optional)</Label>
              <Input
                id="edit-menu-description"
                value={menuForm.description}
                maxLength={500}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMenuForm((prev) => ({ ...prev, description: e.target.value }))}
                placeholder="e.g. Served from 5pm"
              />
            </div>
            {dialogError && <p className="text-sm text-destructive">{dialogError}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMenuDialog(false)} disabled={busy}>Cancel</Button>
            <Button onClick={saveMenuDetails} disabled={busy || !menuForm.name.trim()}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Section create/edit */}
      <Dialog open={!!sectionDialog} onOpenChange={(open: boolean) => !open && setSectionDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{sectionDialog?.section ? "Edit section" : "Add a section"}</DialogTitle>
            <DialogDescription>
              Sections split a menu into parts — Starters, Mains, Desserts, Sides…
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="menu-section-name">Title</Label>
              <Input
                id="menu-section-name"
                value={sectionForm.name}
                maxLength={100}
                autoFocus
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSectionForm((prev) => ({ ...prev, name: e.target.value }))}
                onKeyDown={(e: React.KeyboardEvent) => e.key === "Enter" && saveSection()}
                placeholder="e.g. Starters"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="menu-section-description">Description (optional)</Label>
              <Input
                id="menu-section-description"
                value={sectionForm.description}
                maxLength={500}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSectionForm((prev) => ({ ...prev, description: e.target.value }))}
                placeholder="Shown under the section title"
              />
            </div>
            {dialogError && <p className="text-sm text-destructive">{dialogError}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSectionDialog(null)} disabled={busy}>Cancel</Button>
            <Button onClick={saveSection} disabled={busy || !sectionForm.name.trim()}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {sectionDialog?.section ? "Save" : "Add section"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add item: from library or new */}
      <AddItemDialog
        open={!!addTarget}
        onClose={() => setAddTarget(null)}
        libraryItems={model.items}
        sectionItemIds={addTarget ? addTarget.items.map((item) => item.id) : []}
        busy={busy}
        onAttach={async (item) => {
          if (!addTarget) return;
          const ok = await mutate(() => attachItem(api, addTarget.id, item.id));
          if (ok) setAddTarget(null);
        }}
        onCreateNew={() => {
          if (!addTarget) return;
          const sectionId = addTarget.id;
          setAddTarget(null);
          setDialogError("");
          setItemDialog({ sectionId });
        }}
      />

      {/* Item create (inline) / edit */}
      <MenuItemDialog
        open={!!itemDialog}
        onClose={() => setItemDialog(null)}
        onSave={saveItem}
        initial={itemDialogInitial}
        allergens={model.allergens}
        dietaryTags={model.dietaryTags}
        api={api}
        saving={busy}
        error={dialogError}
      />

      <ConfirmDeleteDialog
        open={!!deleteSectionTarget}
        onOpenChange={(open) => !open && setDeleteSectionTarget(null)}
        title={`Delete "${deleteSectionTarget?.name}"?`}
        description={
          <>
            This removes the section from this menu. Its {deleteSectionTarget?.items.length ?? 0} dish
            {deleteSectionTarget?.items.length === 1 ? " stays" : "es stay"} in your items list.
          </>
        }
        confirmLabel="Delete section"
        busy={busy}
        onConfirm={async () => {
          if (!deleteSectionTarget) return;
          const ok = await mutate(() => deleteSection(api, deleteSectionTarget.id));
          if (ok) setDeleteSectionTarget(null);
        }}
      />
    </div>
  );
}

export function MenuEditorPage() {
  return (
    <MenuModuleGuard title="Menu" description="Edit this menu.">
      {(ctx) => <MenuEditor api={ctx.api} refreshStatus={ctx.refreshStatus} setBreadcrumbs={ctx.setBreadcrumbs} />}
    </MenuModuleGuard>
  );
}
